// MobilePinEntry — folha (bottom-sheet) de PIN do perfil na superfície MOBILE.
// docs/plan/pin-opcional-perfil.md §6.2. Touch puro (sem Norigin/foco de remoto).
//
// Espelha o PinEntry da TV em PROPÓSITO, mas com UI própria touch-first: input
// numérico nativo (o teclado do SO sobe sozinho), dots de progresso e alvos
// >= 44px. Três propósitos num só componente:
//   - 'set':    digite -> confirme (2x). Bate => onSubmit(pin). Diverge => erro
//               local e volta pra etapa 1 (não vai ao backend).
//   - 'verify': 1 etapa. Coleta o PIN e chama onSubmit(pin) (o pai verifica no
//               backend e injeta `error` + bumpa `resetSignal`). Mostra o link
//               "Esqueci o PIN" que abre o sub-fluxo de recuperação por email.
//   - recuperação (só no 'verify'): "Esqueci" -> pede o código por email
//               (onRequestRecover) -> tela pra digitar o código (6 chars
//               ALFANUMÉRICOS, ex: A7K2P9) -> onConfirmRecover(code). Em sucesso
//               o backend REMOVE o PIN; aqui mostramos a tela "PIN removido" e o
//               pai entra/redefine.
//
// SEGURANÇA: o PIN só vive no state local enquanto digitado; nunca é logado nem
// persistido. O componente NÃO guarda o PIN/código após o submit (o pai descarta).

import { useTranslation } from '@app-iptv/i18n'
import { useEffect, useRef, useState } from 'react'

import type { ConfirmPinRecoverResult } from '../sync'

import styles from './MobilePinEntry.module.css'

const PIN_LENGTH = 4
export const CODE_LENGTH = 6

/**
 * Sanitiza o que o usuário digita no campo do CÓDIGO de recuperação. O código é
 * ALFANUMÉRICO (alfabeto maiúsculo sem ambíguos, ex: A7K2P9): filtra qualquer
 * coisa que não seja [A-Za-z0-9], força uppercase (o usuário pode digitar em
 * minúsculas) e limita ao tamanho do código. NÃO confundir com o PIN, que é
 * numérico de 4 dígitos e tem seu próprio filtro (\D).
 */
export function sanitizeRecoverCode(raw: string): string {
  return raw
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()
    .slice(0, CODE_LENGTH)
}

export interface MobilePinEntryProps {
  purpose: 'verify' | 'set'
  /** Nome do perfil (subtítulo contextual). */
  profileName: string
  /** Chamado com o PIN final (4 dígitos). No 'set', só após confirmar 2x. */
  onSubmit: (pin: string) => void
  onCancel: () => void
  /** Erro vindo do pai (ex.: backend "PIN incorreto"/"bloqueado"). Mostrado abaixo. */
  error?: string | null | undefined
  /** Bump deste número pelo pai pra LIMPAR o campo (após erro do backend). */
  resetSignal?: number | undefined
  /** Desabilita o submit enquanto a chamada está em voo (evita duplo POST). */
  busy?: boolean | undefined
  /** Só relevante no 'verify': dispara o email de recuperação. Retorna true se
   *  a requisição foi aceita. Ausente => o link "Esqueci o PIN" não aparece. */
  onRequestRecover?: (() => Promise<boolean>) | undefined
  /** Só relevante no 'verify': confirma o código de recuperação (remove o PIN). */
  onConfirmRecover?: ((code: string) => Promise<ConfirmPinRecoverResult>) | undefined
  /** Chamado quando a recuperação removeu o PIN com sucesso (o pai entra/redefine). */
  onRecovered?: (() => void) | undefined
}

type Stage =
  | { kind: 'enter' } // verify: digitar; set: 1ª etapa
  | { kind: 'confirm' } // set: 2ª etapa
  | { kind: 'recover-code' } // recuperação: digitar o código recebido
  | { kind: 'recovered' } // recuperação concluída (PIN removido)

export function MobilePinEntry({
  purpose,
  profileName,
  onSubmit,
  onCancel,
  error,
  resetSignal,
  busy,
  onRequestRecover,
  onConfirmRecover,
  onRecovered,
}: MobilePinEntryProps): JSX.Element {
  const { t } = useTranslation()
  const [stage, setStage] = useState<Stage>({ kind: 'enter' })
  const [pin, setPin] = useState('')
  const [firstPin, setFirstPin] = useState('') // só no 'set'
  const [code, setCode] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)
  const [recoverInfo, setRecoverInfo] = useState<string | null>(null)
  const [recovering, setRecovering] = useState(false) // chamada de recover em voo
  const pinInputRef = useRef<HTMLInputElement>(null)
  const codeInputRef = useRef<HTMLInputElement>(null)

  // Foca o campo da etapa atual ao abrir e a cada troca de etapa (faz o teclado
  // nativo subir no touch).
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      if (stage.kind === 'recover-code') codeInputRef.current?.focus()
      else if (stage.kind === 'enter' || stage.kind === 'confirm') pinInputRef.current?.focus()
    })
    return () => cancelAnimationFrame(id)
  }, [stage.kind])

  // Pai sinalizou reset (erro do backend no verify/set): limpa o campo do PIN.
  useEffect(() => {
    if (resetSignal === undefined) return
    setPin('')
    const id = requestAnimationFrame(() => pinInputRef.current?.focus())
    return () => cancelAnimationFrame(id)
  }, [resetSignal])

  const handlePinChange = (raw: string): void => {
    const digits = raw.replace(/\D/g, '').slice(0, PIN_LENGTH)
    setPin(digits)
    if (localError) setLocalError(null)
  }

  // O código de recuperação é ALFANUMÉRICO (ex: A7K2P9); ver sanitizeRecoverCode.
  const handleCodeChange = (raw: string): void => {
    setCode(sanitizeRecoverCode(raw))
    if (localError) setLocalError(null)
  }

  const pinComplete = pin.length === PIN_LENGTH
  const codeComplete = code.length === CODE_LENGTH

  const submitPin = (): void => {
    if (!pinComplete || busy) return
    if (purpose === 'verify') {
      onSubmit(pin)
      return
    }
    // purpose === 'set'
    if (stage.kind === 'enter') {
      setFirstPin(pin)
      setPin('')
      setStage({ kind: 'confirm' })
      return
    }
    // stage === 'confirm'
    if (pin !== firstPin) {
      setLocalError(t('pin.mismatch'))
      setFirstPin('')
      setPin('')
      setStage({ kind: 'enter' })
      return
    }
    onSubmit(pin)
  }

  // --- Recuperação por email ---
  const startRecover = async (): Promise<void> => {
    if (!onRequestRecover || recovering) return
    setRecovering(true)
    setLocalError(null)
    setRecoverInfo(null)
    const ok = await onRequestRecover()
    setRecovering(false)
    if (ok) {
      setCode('')
      setRecoverInfo(t('pin.recoverSent'))
      setStage({ kind: 'recover-code' })
    } else {
      setLocalError(t('pin.recoverSendError'))
    }
  }

  const resendRecover = async (): Promise<void> => {
    if (!onRequestRecover || recovering) return
    setRecovering(true)
    setLocalError(null)
    const ok = await onRequestRecover()
    setRecovering(false)
    setRecoverInfo(ok ? t('pin.recoverSent') : null)
    if (!ok) setLocalError(t('pin.recoverSendError'))
  }

  const confirmRecover = async (): Promise<void> => {
    if (!onConfirmRecover || !codeComplete || recovering) return
    setRecovering(true)
    setLocalError(null)
    const res = await onConfirmRecover(code)
    setRecovering(false)
    if (res.status === 'ok') {
      setCode('')
      setRecoverInfo(null)
      setStage({ kind: 'recovered' })
    } else if (res.status === 'invalid_code') {
      setCode('')
      setLocalError(t('pin.codeInvalid'))
    } else {
      setLocalError(t('pin.recoverError'))
    }
  }

  const shownError = error ?? localError

  // ----- Tela: recuperação concluída (PIN removido) -----
  if (stage.kind === 'recovered') {
    return (
      <Backdrop label={t('pin.removedTitle')} onCancel={onCancel}>
        <h3 className={styles.title}>{t('pin.removedTitle')}</h3>
        <p className={styles.subtitle}>{profileName}</p>
        <p className={styles.body}>{t('pin.removedBody')}</p>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btnPrimary}
            data-action="pin-recovered-ok"
            onClick={() => (onRecovered ? onRecovered() : onCancel())}
          >
            {t('common.confirm')}
          </button>
        </div>
      </Backdrop>
    )
  }

  // ----- Tela: digitar o código de recuperação -----
  if (stage.kind === 'recover-code') {
    return (
      <Backdrop label={t('pin.recoverTitle')} onCancel={onCancel}>
        <h3 className={styles.title}>{t('pin.recoverTitle')}</h3>
        <p className={styles.subtitle}>{profileName}</p>
        <p className={styles.body}>{t('pin.recoverBody')}</p>
        {recoverInfo && (
          <p className={styles.info} role="status" aria-live="polite">
            {recoverInfo}
          </p>
        )}
        <input
          ref={codeInputRef}
          className={styles.codeInput}
          type="text"
          inputMode="text"
          autoComplete="one-time-code"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          maxLength={CODE_LENGTH}
          value={code}
          aria-label={t('pin.codeLabel')}
          placeholder={t('pin.codePlaceholder')}
          data-field="pin-code"
          onChange={(e) => handleCodeChange(e.target.value)}
        />
        {shownError && (
          <p className={styles.error} role="alert" aria-live="polite">
            {shownError}
          </p>
        )}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btnPrimary}
            data-action="pin-recover-confirm"
            disabled={!codeComplete || recovering}
            onClick={() => void confirmRecover()}
          >
            {recovering ? t('pin.recoverConfirming') : t('pin.recoverConfirm')}
          </button>
          <button
            type="button"
            className={styles.btnGhost}
            data-action="pin-recover-resend"
            disabled={recovering}
            onClick={() => void resendRecover()}
          >
            {t('pin.recoverSend')}
          </button>
          <button type="button" className={styles.btnGhost} onClick={onCancel}>
            {t('common.cancelAction')}
          </button>
        </div>
      </Backdrop>
    )
  }

  // ----- Tela: digitar/confirmar o PIN (set/verify) -----
  const title =
    purpose === 'verify'
      ? t('pin.enterTitle')
      : stage.kind === 'enter'
        ? t('pin.setTitle')
        : t('pin.confirmTitle')
  const primaryLabel =
    purpose === 'set' && stage.kind === 'enter' ? t('pin.continue') : t('common.confirm')

  return (
    <Backdrop label={title} onCancel={onCancel}>
      <h3 className={styles.title}>{title}</h3>
      <p className={styles.subtitle}>{profileName}</p>

      {/* Dots de progresso — feedback de quantos dígitos já entraram. */}
      <div className={styles.dots} aria-hidden="true">
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <span key={i} className={`${styles.dot} ${i < pin.length ? styles.dotFilled : ''}`} />
        ))}
      </div>

      {/* Input numérico nativo (mascarado). O teclado do SO sobe no foco. */}
      <input
        ref={pinInputRef}
        className={styles.pinInput}
        type="password"
        inputMode="numeric"
        autoComplete="off"
        pattern="[0-9]*"
        maxLength={PIN_LENGTH}
        value={pin}
        aria-label={title}
        placeholder={t('pin.placeholder')}
        data-field="pin"
        onChange={(e) => handlePinChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submitPin()
        }}
      />

      {shownError && (
        <p className={styles.error} role="alert" aria-live="polite">
          {shownError}
        </p>
      )}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.btnPrimary}
          data-action="pin-submit"
          disabled={!pinComplete || busy}
          onClick={submitPin}
        >
          {primaryLabel}
        </button>
        {/* "Esqueci o PIN" só no verify e se o pai forneceu o handler de recover. */}
        {purpose === 'verify' && onRequestRecover && (
          <button
            type="button"
            className={styles.btnLink}
            data-action="pin-forgot"
            disabled={recovering}
            onClick={() => void startRecover()}
          >
            {recovering ? t('pin.recoverSending') : t('pin.forgot')}
          </button>
        )}
        <button type="button" className={styles.btnGhost} onClick={onCancel}>
          {t('common.cancelAction')}
        </button>
      </div>
    </Backdrop>
  )
}

/** Backdrop + folha (mesma convenção visual dos sheets do MobileProfile). O toque
 *  no fundo cancela; o toque na folha não propaga. */
function Backdrop({
  label,
  onCancel,
  children,
}: {
  label: string
  onCancel: () => void
  children: React.ReactNode
}): JSX.Element {
  return (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      data-overlay="pin"
      onClick={onCancel}
    >
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  )
}
